// Shared icon-picker STORE — one modal for the whole page, opened from anywhere.
//
// Design rationale (this is a deliberate rewrite of the old per-instance popover):
// the previous version mounted one `@IconPicker` Alpine COMPONENT per consumer,
// each teleporting its own modal to <body>. Inside a SortableJS-managed list that
// teleport got re-processed on every drag/reorder, duplicating modal DOM into
// <body> without bound → renderer OOM ("Aw, Snap"). It also routed selection
// through a bubbling `icon-selected` event dispatched on `$root`, which does not
// reliably resolve back across the teleport boundary — so picks silently no-op'd.
//
// This store fixes both structurally:
//   - ONE modal (a native <dialog>, rendered once per page via @IconPickerModal).
//     Native <dialog>.showModal() gives top-layer rendering, backdrop, ESC, and
//     focus-trapping for free — no z-index, no scroll-lock hacks, no teleport.
//   - Selection is a DIRECT CALLBACK (`_onPick`), not an event. The trigger passes
//     a closure; we call it on pick. No event routing, no $root, no teleport.
//   - The catalog (GET /api/icons) is fetched once and shared by every consumer.
//
// Consumers: a trigger button (see @IconTriggerButton) calls
// `$store.iconPicker.openFor(currentName, name => { ...write it... })`.

const DIALOG_ID = "cms-icon-picker";

interface CatalogIcon {
  name: string;
  lucide: string;
  category: string;
  keywords?: string[];
}

interface CatalogCategory {
  key: string;
  label_de: string;
  label_en: string;
}

interface IconCatalog {
  categories: CatalogCategory[];
  icons: CatalogIcon[];
}

// Module-level shared promise so the catalog is fetched exactly once per page,
// regardless of how many triggers exist. A transient failure resets the promise
// so the next open() retries instead of caching the rejection forever.
let catalogPromise: Promise<IconCatalog> | null = null;

function loadCatalog(): Promise<IconCatalog> {
  if (!catalogPromise) {
    catalogPromise = fetch("/api/icons", { credentials: "same-origin" })
      .then((r) => {
        if (!r.ok) throw new Error(`GET /api/icons: ${r.status}`);
        return r.json() as Promise<IconCatalog>;
      })
      .catch((err) => {
        catalogPromise = null;
        throw err;
      });
  }
  return catalogPromise;
}

function dialogEl(): HTMLDialogElement | null {
  return typeof document !== "undefined"
    ? (document.getElementById(DIALOG_ID) as HTMLDialogElement | null)
    : null;
}

// iconPickerStore is registered once via Alpine.store('iconPicker', iconPickerStore()).
export function iconPickerStore() {
  return {
    // --- reactive state (drives the modal markup) ---
    value: "" as string, // currently-selected name (for grid highlight)
    query: "" as string,
    activeCategory: "" as string,
    categories: [] as CatalogCategory[],
    icons: [] as CatalogIcon[],
    loaded: false,

    // The pick callback supplied by whoever opened the modal. Held as a plain
    // reference (functions aren't deep-reactive, which is exactly what we want).
    _onPick: null as ((name: string) => void) | null,

    get lang(): string {
      return (typeof document !== "undefined" && document.documentElement.lang) || "en";
    },

    async ensureLoaded() {
      if (this.loaded) return;
      try {
        const cat = await loadCatalog();
        this.categories = cat.categories || [];
        this.icons = cat.icons || [];
        if (!this.activeCategory && this.categories.length > 0) {
          this.activeCategory = this.categories[0].key;
        }
        this.loaded = true;
      } catch {
        // Leave loaded=false so a later openFor() retries; grid shows empty state.
      }
    },

    // openFor seeds the modal for one selection and shows it. `onPick` runs with
    // the chosen icon name when the user picks; it is the consumer's whole write
    // path (e.g. `row.icon = name; emit()`), so there is no shared mutable state
    // to get out of sync.
    openFor(current: string, onPick: (name: string) => void) {
      this.value = current || "";
      this._onPick = onPick;
      this.query = "";
      void this.ensureLoaded();
      dialogEl()?.showModal();
    },

    close() {
      dialogEl()?.close();
    },

    // onClosed runs from the <dialog>'s native 'close' event (ESC, backdrop, or
    // our close()). Clear the callback so a stale closure can't fire later.
    onClosed() {
      this._onPick = null;
      this.query = "";
    },

    pick(name: string) {
      const cb = this._onPick;
      this.close();
      cb?.(name);
    },

    categoryLabel(cat: CatalogCategory): string {
      return this.lang === "de" ? cat.label_de : cat.label_en;
    },

    // When searching, match name + bilingual keywords + category. Otherwise show
    // only the active category (keeps the rendered count small and snappy).
    filtered(): CatalogIcon[] {
      const list = this.icons;
      const q = this.query.toLowerCase().trim();
      if (q) {
        return list.filter(
          (i) =>
            i.name.toLowerCase().includes(q) ||
            (i.keywords || []).some((k) => k.toLowerCase().includes(q)) ||
            (i.category || "").toLowerCase().includes(q),
        );
      }
      if (this.activeCategory) {
        return list.filter((i) => i.category === this.activeCategory);
      }
      return list;
    },
  };
}
